import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Wrench, MapPin, CurrencyDollar } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const STRATEGIES = [
  ["budget", "Budget / lower-market"],
  ["competitive", "Competitive"],
  ["average", "Average-market"],
  ["premium", "Premium"],
  ["custom", "Custom % of market average"],
];

export default function Onboarding() {
  const nav = useNavigate();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/settings").then((r) => setS(r.data)); }, []);
  if (!s) return <div className="p-10 text-center text-zinc-400">Loading…</div>;

  const set = (p) => setS({ ...s, ...p });
  const finish = async () => {
    if (!s.business_name?.trim()) { toast.error("Please enter your business name."); return; }
    if (!s.business_zip?.trim()) { toast.error("Please enter your business ZIP code."); return; }
    setSaving(true);
    try { await api.put("/settings", s); toast.success("You're all set!"); nav("/"); }
    catch (e) { toast.error("Could not save. Try again."); }
    setSaving(false);
  };

  return (
    <div className="min-h-screen">
      <header className="px-5 pt-10 pb-4">
        <div className="w-14 h-14 rounded-2xl bg-orange-600 text-white flex items-center justify-center mb-4"><Wrench size={30} weight="fill" /></div>
        <h1 className="font-heading text-3xl font-black tracking-tight">Set up your service area</h1>
        <p className="text-sm text-zinc-500 mt-1">This powers accurate local pricing research for every job.</p>
      </header>

      <div className="px-4 space-y-5">
        <section className="bg-white border border-zinc-200 rounded-xl p-4 space-y-3">
          <F label="Business Name"><Input data-testid="onb-name" value={s.business_name || ""} onChange={(e) => set({ business_name: e.target.value })} className="h-12" /></F>
          <div className="grid grid-cols-2 gap-3">
            <F label="Business ZIP"><Input data-testid="onb-zip" value={s.business_zip || ""} onChange={(e) => set({ business_zip: e.target.value })} placeholder="97201" className="h-12" /></F>
            <F label="Max Radius (mi)"><Input data-testid="onb-radius" type="number" value={s.max_radius ?? 25} onChange={(e) => set({ max_radius: Number(e.target.value) })} className="h-12" /></F>
          </div>
          <F label="Primary Service Area"><Input data-testid="onb-area" value={s.service_area || ""} onChange={(e) => set({ service_area: e.target.value })} placeholder="Portland, OR metro" className="h-12" /></F>
          <F label="Trades / Services (comma separated)"><Input data-testid="onb-trades" value={(s.trades || []).join(", ")} onChange={(e) => set({ trades: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} placeholder="plumbing, electrical, HVAC, carpentry" className="h-12" /></F>
        </section>

        <section className="bg-white border border-zinc-200 rounded-xl p-4 space-y-3">
          <p className="text-xs tracking-[0.15em] uppercase font-bold text-zinc-400">Pricing Preference</p>
          <select data-testid="onb-strategy" value={s.pricing_strategy || "average"} onChange={(e) => set({ pricing_strategy: e.target.value })} className="h-12 w-full border border-zinc-200 rounded-lg px-3 bg-white text-sm font-medium">
            {STRATEGIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          {s.pricing_strategy === "custom" && (
            <F label="Custom % of market average"><Input data-testid="onb-custompct" type="number" value={s.custom_pct ?? 100} onChange={(e) => set({ custom_pct: Number(e.target.value) })} className="h-12" /></F>
          )}
        </section>

        <Button data-testid="onb-finish" onClick={finish} disabled={saving} className="w-full h-14 font-bold text-base">
          {saving ? "Saving…" : "Start using Job Spoken"}
        </Button>
      </div>
    </div>
  );
}

const F = ({ label, children }) => (
  <div><label className="text-[11px] tracking-[0.12em] uppercase font-bold text-zinc-400 block mb-1">{label}</label>{children}</div>
);
